'use client';

import { useTranslations } from 'next-intl';
import {
  Package,
  Printer,
  Container,
  FileStack,
  X,
  type LucideIcon
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Progress } from '@/components/ui/progress';
import { type ScanBatch, type BatchPageRecord } from '../types';
import { DwtScannerPanel } from './dwt-scanner-panel';
import { DevQrInjectorPanel } from './dev-qr-injector-panel';

// ─── Props ───────────────────────────────────────────────────────────────────
interface ScanRightPanelProps {
  batch: (ScanBatch & { pages: BatchPageRecord[] }) | undefined;
  batchId: number;
  nextSequence: number;
  total: number;
  // Counters (mobile progress strip)
  progressPct: number;
  failed: number;
  success: number;
  remaining: number;
  isComplete: boolean;
  // When the batch has been handed off to QA the scanner section is hidden.
  isHandedOff?: boolean;
  // Hardware-scanner capture mode (configurations toggle). When false, the
  // «الماسح» document-scanner section is hidden and the operator captures
  // pages via manual upload instead.
  scannerEnabled?: boolean;
  // QR-quarantine intake — forwarded straight to the embedded scanner panel so
  // acquired pages flow through the parent's `intakeImages` funnel (clean
  // pages added instantly, problems held back) instead of uploading directly.
  onScannerAcquire?: (
    blob: Blob,
    opts?: { replaceItemId?: string }
  ) => void | Promise<void>;
  scannerRescanTargetId?: string | null;
  onScannerAcquireDone?: () => void;
  // Desktop collapse: when `collapsed`, the panel hides itself on lg (a thin
  // re-open strip is rendered in its place by the parent). Mobile always shows
  // the full panel — it is the primary mobile navigation surface.
  collapsed?: boolean;
  onCollapse?: () => void;
}

// ─── Component ───────────────────────────────────────────────────────────────
/**
 * Left-side scanning panel: batch metadata pills + the hardware document
 * scanner («الماسح»). The per-page list (filters, status overlay, delete /
 * replace / re-scan) now lives entirely in {@link PageThumbnailRail}.
 */
export function ScanRightPanel({
  batch,
  batchId,
  nextSequence,
  total,
  progressPct,
  failed,
  success,
  remaining,
  isComplete,
  isHandedOff = false,
  scannerEnabled = true,
  onScannerAcquire,
  scannerRescanTargetId,
  onScannerAcquireDone,
  collapsed = false,
  onCollapse
}: ScanRightPanelProps) {
  const t = useTranslations('photoScanning');

  return (
    <div
      className={cn(
        'order-first flex min-w-0 flex-col overflow-auto border-b lg:order-none lg:h-full lg:border-b-0',
        // Collapsed only affects desktop; mobile keeps the full panel.
        collapsed && 'lg:hidden'
      )}
    >
      {/* Desktop-only panel header with a collapse control, so the toggle
          lives next to its section. Hidden on mobile (no collapse there). */}
      {onCollapse && (
        <div className='hidden shrink-0 items-center justify-between border-b px-3 py-2 lg:flex'>
          <span className='text-xs font-semibold text-slate-600 dark:text-slate-300'>
            {t('sidePanelTitle')}
          </span>
          <button
            type='button'
            onClick={onCollapse}
            title={t('collapseSidePanel')}
            aria-label={t('collapseSidePanel')}
            className='inline-flex h-6 w-6 items-center justify-center rounded text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-200'
          >
            <X className='h-3.5 w-3.5' aria-hidden />
          </button>
        </div>
      )}

      {/* Mobile-only compact progress strip */}
      <div className='shrink-0 border-b p-3 lg:hidden'>
        <div className='mb-1.5 flex items-center justify-between text-xs'>
          <span className='font-medium text-slate-600 dark:text-slate-300'>
            {t('scanProgress')}
          </span>
          <span className='font-bold text-slate-700 dark:text-slate-200'>
            {progressPct}%
          </span>
        </div>
        <Progress value={progressPct} className='h-1.5' />
        <div className='mt-2 flex justify-between text-[10px] text-slate-500'>
          <span className='text-rose-500'>
            {failed} {t('failedCounter')}
          </span>
          <span className='text-emerald-500'>
            {success} {t('successCounter')}
          </span>
          <span>
            {remaining} {t('remainingCounter')}
          </span>
        </div>
      </div>

      {/* Batch metadata — compact key/value block (scannable, fits a narrow
          column better than wrapped pills). */}
      <div className='shrink-0 border-b px-3 py-2.5'>
        <dl className='grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 gap-y-1.5 text-[11px]'>
          <MetaRow
            icon={Package}
            label={t('boxId')}
            value={batch?.box_id ?? '—'}
          />
          <MetaRow
            icon={Printer}
            label={t('scannerLabel')}
            value={batch ? `${batch.scanner_id} — ${batch.scanner_model}` : '—'}
          />
          <MetaRow
            icon={Container}
            label={t('columnContainerId')}
            value={batch?.container_id ?? '—'}
          />
          <MetaRow
            icon={FileStack}
            label={t('totalPages')}
            value={batch ? t('pagesLabel', { count: batch.total_pages }) : '—'}
          />
        </dl>
      </div>

      {/* Dev-only QR injector — hidden in production (renders nothing for
          non-dev users). Kept available in BOTH capture modes because it also
          bakes a synthetic QR into manually-uploaded pages, not just scans. */}
      {!isComplete && !isHandedOff && (
        <DevQrInjectorPanel className='mx-3 my-3' />
      )}

      {/* Document scanner — only in scanner capture mode */}
      {scannerEnabled && !isComplete && !isHandedOff && (
        <div className='shrink-0 border-t'>
          <DwtScannerPanel
            batchId={batchId}
            nextSequence={nextSequence}
            total={total}
            isDoubleSided={batch?.is_double_sided ?? false}
            containerId={batch?.container_id ?? null}
            onAcquireBlob={onScannerAcquire}
            rescanTargetId={scannerRescanTargetId}
            onAcquireDone={onScannerAcquireDone}
          />
        </div>
      )}
    </div>
  );
}

// ─── Metadata row ──────────────────────────────────────────────────────────
function MetaRow({
  icon: Icon,
  label,
  value
}: {
  icon: LucideIcon;
  label: string;
  value: string;
}) {
  return (
    <>
      <dt className='flex items-center gap-1.5 text-slate-400 dark:text-slate-500'>
        <Icon className='h-3 w-3 shrink-0' aria-hidden />
        <span>{label}</span>
      </dt>
      <dd
        className='truncate text-end font-mono text-slate-700 dark:text-slate-200'
        title={value}
      >
        {value}
      </dd>
    </>
  );
}
